import useSWRInfinite, {
  SWRInfiniteResponse,
  unstable_serialize,
} from 'swr/infinite'
import { useCallback } from 'react'
import {
  getAuthorizationCacheScope,
  getRpcAccess,
  isPermissionDenied,
  RpcAccessDenied,
} from '../../app/AppAuth/rpcAuthorization'
import { useAuth, useAuthHeader } from '../../context/authContext'
import { useCurrentAuth } from '../../context/AuthSession'
import { getRefererHeader } from '../../adapter/referer'
import { callGrpc, SwrGrpcResponse, AuthorizedQueryState } from './useSwrGrpc'
import { GrpcCall, useGetInfiniteCacheKey } from './utils'
import { queue } from './queue'
import { useQuerySubscribers } from './querySubscribers'
import {
  RpcQueryCancelled,
  useQueryErrorHandler,
  useRecoverCancelledQuery,
} from './queryCancellation'

/**
  Hook for using SWR with pagination
  For an example of working with this hook see useChangeLogEntries.tsx
  ** This does not include logic for audit specific request. You will have to handle that in your request callback function
  * @param {GrpcCall} asyncCall - the grpc method that you want to call
  * @param {function requestFunc(pageNumber:number) {
    return Request
  }} - callback function that returns the request with the passed in page number added. See useChangeLogEntries.tsx
  * @param {SWRConfiguration} config - optional with options like disabling revalidation on focus. See SWR docs
  * @returns {SWRInfiniteResponse} { response }
*/

export const useSwrInfiniteGrpc = <Request, Response>(
  asyncCall: GrpcCall<Request, Response>,
  requestFunc: () => Request,
  pageNumber: number,
  config?: SwrGrpcResponse,
  shouldFetch = true,
): { response: SWRInfiniteResponse<Response> & AuthorizedQueryState } => {
  const auth = useAuth()
  const access = getRpcAccess(asyncCall, auth, requestFunc())
  const enabled = shouldFetch && access === 'allowed'
  const scope = getAuthorizationCacheScope(auth)
  const currentAuth = useCurrentAuth(auth)
  const onError = useQueryErrorHandler(config)
  const header = {
    ...useAuthHeader(),
    ...getRefererHeader(),
  }
  const getInfiniteCacheKey = useGetInfiniteCacheKey()
  const getKey = () =>
    enabled ? getInfiniteCacheKey(asyncCall, requestFunc(), pageNumber) : null
  const queryKey = enabled ? unstable_serialize(getKey) : ''
  // All pages share the subscription to SWR Infinite's aggregate cache entry.
  const hasSubscribers = useQuerySubscribers(queryKey)
  const isActive = useCallback(
    () =>
      hasSubscribers() &&
      !!currentAuth() &&
      getAuthorizationCacheScope(currentAuth()) === scope,
    [currentAuth, scope, hasSubscribers],
  )
  const response = useSWRInfinite<Response>(
    getKey,
    (_method: string, _request: unknown, _accountId: string) =>
      queue.add(async () => {
        const request = requestFunc()
        if (!isActive()) throw new RpcQueryCancelled()
        if (getRpcAccess(asyncCall, auth, request) !== 'allowed')
          throw new RpcAccessDenied(asyncCall)
        const data = await callGrpc(asyncCall, request, header, auth).catch(
          (error: unknown) => {
            if (!isActive()) throw new RpcQueryCancelled()
            throw error
          },
        )
        if (!isActive()) throw new RpcQueryCancelled()
        return data
      }) as Promise<Response>,
    {
      ...config,
      keepPreviousData: false,
      onError,
      shouldRetryOnError: (error) => {
        if (
          error instanceof RpcQueryCancelled ||
          isPermissionDenied(error) ||
          !isActive()
        )
          return false
        return typeof config?.shouldRetryOnError === 'function'
          ? config.shouldRetryOnError(error)
          : config?.shouldRetryOnError !== false
      },
    },
  )
  const { mutate: swrMutate } = response
  const mutate = useCallback(
    (...args: Parameters<typeof swrMutate>) => {
      if (!enabled || !isActive()) return Promise.resolve(undefined)
      return swrMutate(...args)
    },
    [swrMutate, enabled, isActive],
  ) as typeof swrMutate
  const cancelled = useRecoverCancelledQuery(
    queryKey,
    response.error,
    enabled,
    response.isValidating,
    isActive,
    mutate,
  )
  return {
    response: {
      ...response,
      data: enabled ? response.data : undefined,
      access,
      isDisabled: !shouldFetch || access === 'denied',
      error: enabled && !cancelled ? response.error : undefined,
      isLoading:
        shouldFetch && access === 'pending'
          ? true
          : enabled && (response.isLoading || (cancelled && !response.data)),
      isValidating: enabled && response.isValidating,
      mutate,
      setSize: (...args) =>
        enabled && isActive()
          ? response.setSize(...args)
          : Promise.resolve(undefined),
    },
  }
}
